/**
 * ProgressionDistanceChoice — Sprint 13 S13.10a (motor v10.1 §2).
 *
 * Pantalla "¿Para qué distancia quieres progresar?". Se muestra cuando el
 * backend señala `progression_distance_choice_pending` (una progresión
 * venció sin carrera y el atleta queda sin plan activo hasta elegir).
 *
 * Al elegir distancia → POST /training-plans con `plan_type='progression'`
 * + `race_distance` (flujo de S13.3, ya existe en la API client). Tras
 * crear, invoca `onCreated` para que el contenedor recargue el plan activo.
 *
 * Distancias en MINÚSCULA (5k/10k/15k/half_marathon/marathon) — fix de
 * casing S13.7 + enum PROGRESSION_DISTANCES del backend. Mismo set que la
 * rama progresión de CreatePlanPage (S13.6a).
 */
import { useState } from "react";

import { createPlan } from "@/api/training";
import { t } from "@/i18n/es";
import { getAllowedRaceDistances } from "@/lib/raceDistance";
import { ApiError, type AthleteLevel } from "@/types/api";

/** Opciones de distancia del Plan de Progresión (valores en minúscula
 *  para coincidir con el enum estricto del backend). */
const PROGRESSION_DISTANCE_OPTIONS: ReadonlyArray<{
  value: string;
  labelKey: keyof typeof t.plan;
}> = [
  { value: "5k", labelKey: "raceDistance5k" },
  { value: "10k", labelKey: "raceDistance10k" },
  { value: "15k", labelKey: "raceDistance15k" },
  { value: "half_marathon", labelKey: "raceDistanceHalfMarathon" },
  { value: "marathon", labelKey: "raceDistanceMarathon" },
];

interface Props {
  /** Llamado tras crear la nueva progresión (el contenedor recarga). */
  onCreated: () => void;
  /** v15.5-§8.4: nivel del atleta para filtrar el dropdown. Pasado por
   *  PlanPage (que ya fetchea onboarding). null = no se filtra (defensa). */
  athleteLevel?: AthleteLevel | null;
}

export function ProgressionDistanceChoice({
  onCreated,
  athleteLevel = null,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // v15.5-§8.4: lista filtrada por nivel. Beginner solo ve "5k".
  const filteredOptions = PROGRESSION_DISTANCE_OPTIONS.filter((opt) =>
    getAllowedRaceDistances(
      athleteLevel,
      PROGRESSION_DISTANCE_OPTIONS.map((o) => o.value),
    ).includes(opt.value),
  );

  async function choose(distance: string) {
    setSubmitting(true);
    setError(null);
    try {
      await createPlan({
        plan_type: "progression",
        race_distance: distance,
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.code === "beginner_max_5k") {
        // v15.5-§8.4: defensa backend (caso raro — el filtro frontend
        // ya debería haber prevenido la elección).
        setError(t.plan.errorBeginnerMax5k);
      } else {
        setError(t.plan.toastError);
      }
      setSubmitting(false);
    }
  }

  return (
    <main
      data-testid="progression-distance-choice"
      className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone"
    >
      <header>
        <p className="text-xs uppercase tracking-widest text-gray-mid">
          {t.plan.pageTitle.toLowerCase()}
        </p>
        <h1 className="mt-6 text-2xl font-medium tracking-tight">
          {t.plan.progressionChoiceTitle}
        </h1>
        <p className="mt-2 text-sm text-bone-muted">
          {t.plan.progressionChoiceSubtitle}
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {filteredOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-testid={`progression-choice-${opt.value}`}
            disabled={submitting}
            onClick={() => void choose(opt.value)}
            className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-3 text-left text-sm text-bone transition-colors duration-fast hover:border-border-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.plan[opt.labelKey]}
          </button>
        ))}
      </div>

      {error && (
        <p
          data-testid="progression-choice-error"
          role="alert"
          className="text-sm text-danger"
        >
          {error}
        </p>
      )}
    </main>
  );
}
